import { useLayoutEffect, useRef, useState, VoidFunctionComponent } from 'react';
import { useFrame } from '@react-three/fiber';
import { BufferAttribute, Group, MathUtils, Mesh, MeshPhysicalMaterial, Shape, ShapeGeometry, Vector2 } from 'three';
import { Text, useTexture } from '@react-three/drei';
import { celophane, garamondNarrowBook } from '@/lib/fonts';
import { permute } from '@/lib/permute';
import { legendaryCard, legendaryWhite, offblack } from '@/lib/colors';
import { CardBounds } from '@/lib/types';
import scrtchedMetalImg from '@/img/roughness-scratched-metal-1024.png';
import { useScratchedMetalRoughness } from '@/hooks/useScratchedMetalRoughness';

const BOX_HEIGHT = 0.145;
const RADIUS = BOX_HEIGHT / 2;
const METABALL_RADIUS = 0.025;
const VALUE_LEFT_MARGIN = 0.022;
const HORIZONTAL_INNER_PADDING = 0.061;
const BLACK_BORDER_SIZE = 0.012;
const MAX_ATTRIBUTE_ROW_SIZE = 1.67;

const drawBackground = (textLeft: number, size: number, textCenterY: number, padding: number = 0): Shape => {
    const shape = new Shape();
    const bottom = textCenterY - RADIUS;
    const top = textCenterY + RADIUS;
    shape.moveTo(textLeft + padding, top + padding);
    shape.absarc(textLeft, textCenterY, RADIUS + padding, Math.PI / 2, (3 * Math.PI) / 2, false);
    shape.lineTo(textLeft + size + padding, bottom - padding);
    shape.absarc(textLeft + size, textCenterY, RADIUS + padding, (3 * Math.PI) / 2, Math.PI / 2, false);
    return shape;
};

// draw our connection between metaballs
// start by drawing a rectangle between each node.
// Nodes are circles of equal radius with their centerX where the text starts or ends and
// their centerY in the middle of the text.
// The circles of neighboring nodes overlap slightly.
// The points of our rectangle are the points where a 3rd circle of equal
// radius would contact both other circles at each circumference on one side
// of the node connection and a 4th circle doing the bottom
// Then, connect points of the rectangle using a quadratic curve
const connectNodes = (
    left1: number,
    centerY1: number,
    size1: number,
    left2: number,
    centerY2: number,
    padding: number = 0
): Shape => {
    const right1 = left1 + size1;
    const top1 = centerY1 + RADIUS;
    const top2 = centerY2 + RADIUS;

    // distance between center of circle for each node
    const deltaGroup = ((left2 - right1) ** 2 + (top2 - top1) ** 2) ** 0.5;

    // angle of vector pointing to our point of intersection
    const theta = Math.asin(deltaGroup / 2 / BOX_HEIGHT);

    // angle between nodes
    const gamma = Math.atan((centerY2 - centerY1) / (left2 - right1));

    const deltaX = RADIUS * Math.sin(theta);
    const deltaY = RADIUS * Math.cos(theta);

    // top left corner of rectangle
    const x1 = right1 + deltaX;
    const y1 = centerY1 + deltaY;
    const topLeft = new Vector2(x1, y1);
    topLeft.rotateAround(new Vector2(right1, centerY1), gamma);

    // top right corner of rectangle
    const x2 = left2 - deltaX;
    const y2 = centerY2 + deltaY;
    const topRight = new Vector2(x2, y2);
    topRight.rotateAround(new Vector2(left2, centerY2), gamma);

    // bottom right corner of rectangle
    const x3 = left2 - deltaX;
    const y3 = centerY2 - deltaY;
    const bottomRight = new Vector2(x3, y3);
    bottomRight.rotateAround(new Vector2(left2, centerY2), gamma);

    // bottom left corner of rectangle
    const x4 = right1 + deltaX;
    const y4 = centerY1 - deltaY;
    const bottomLeft = new Vector2(x4, y4);
    bottomLeft.rotateAround(new Vector2(right1, centerY1), gamma);

    const shape = new Shape();
    shape.moveTo(topLeft.x, topLeft.y + padding);
    shape.quadraticCurveTo(
        topLeft.x + (topRight.x - topLeft.x) / 2,
        topLeft.y + (topRight.y - topLeft.y) / 2 + padding - METABALL_RADIUS,
        topRight.x,
        topRight.y + padding
    );
    shape.lineTo(bottomRight.x, bottomRight.y - padding);
    shape.quadraticCurveTo(
        bottomRight.x + (bottomLeft.x - bottomRight.x) / 2,
        bottomRight.y - (bottomRight.y - bottomLeft.y) / 2 - padding + METABALL_RADIUS,
        bottomLeft.x,
        bottomLeft.y - padding
    );

    return shape;
};

const sumSizes = (totalSizes: number, { size }: { size: number }): number => (totalSizes += size);

const doesRowFit = (row: AttributeSize[]): boolean => row.reduce(sumSizes, 0) < MAX_ATTRIBUTE_ROW_SIZE;

// returns an array of arrays of attributes
// the first element is the attributes that should appear in the 1st row
// the second element is the attributes that should appear in the 2nd row
// the third element is the attributes that should be hidden from the page
const findSizePermutation = (
    sizes: AttributeSize[],
    attributesToHide: AttributeSize[] = []
): [AttributeSize[], AttributeSize[], AttributeSize[]] => {
    // remove all attributes that are too big on their own
    const biggerThanRow = sizes.filter((size) => !doesRowFit([size]));
    const hiddenAttributes = [...attributesToHide, ...biggerThanRow];
    const smallerThanRow = sizes.filter((size) => doesRowFit([size]));
    const permutator = permute(smallerThanRow);
    for (const sizesArray of permutator) {
        const firstRow = sizesArray.slice(0, 3);
        const secondRow = sizesArray.slice(3, 5);
        if (doesRowFit(firstRow) && doesRowFit(secondRow)) {
            return [firstRow, secondRow, hiddenAttributes];
        }
    }

    // drop an attribute if all don't fit
    if (sizes.length >= 4) {
        for (let i = sizes.length - 1; i >= 0; i--) {
            const trySizes = [...sizes];
            trySizes.splice(i, 1);
            const [firstRow, secondRow, hidden] = findSizePermutation(trySizes, hiddenAttributes);
            if (firstRow.length) {
                return [firstRow, secondRow, [...hidden, sizes[i]]];
            }
        }
    }

    // if we have 2 or 3 attributes that don't fit onto a single row, split across rows
    if (sizes.length === 2 || sizes.length === 3) {
        const sortedSizes = [...sizes];
        const biggestAttribute = sortedSizes.sort((a, b) => b.size - a.size)[0];

        // if our biggest attribute doesn't fit in a row, remove it
        if (!doesRowFit([biggestAttribute])) {
            const rowWithoutBiggest = sortedSizes.filter(({ refIndex }) => refIndex !== biggestAttribute.refIndex);
            const [firstRow, secondRow, hidden] = findSizePermutation(rowWithoutBiggest, hiddenAttributes);
            return [firstRow, secondRow, [...hidden, biggestAttribute]];
        }

        // put last item on 2nd row, see if it fits
        const firstRow = sizes.slice(0, -1);
        const secondRow = sizes.slice(-1);
        // if our new top row fits
        if (doesRowFit(firstRow) && doesRowFit(secondRow)) {
            return [firstRow, secondRow, hiddenAttributes];
        }
    }
    // if we failed to come up with an attribute permutation, hide em all!
    return [[], [], sizes];
};

const calculateWidth = (mesh: Mesh): number => {
    const { boundingBox } = mesh.geometry;
    return boundingBox ? boundingBox.max.x - boundingBox.min.x : 0;
};

interface Attribute {
    label: string;
    value: string;
}

interface AttributeRefs {
    label: Mesh;
    value: Mesh;
    group: Group;
}

interface AttributeSize {
    refIndex: number;
    size: number;
}

interface Props {
    attributes: Attribute[];
    width: number;
    legendary: boolean;
    cardBounds: CardBounds;
}

export const AttributeGroup: VoidFunctionComponent<Props> = ({ attributes, width, legendary, cardBounds }) => {
    const groupRef = useRef<Group>(new Group());
    const backgroundRef = useRef<Mesh>(new Mesh());
    const [bgShape, setBgShape] = useState<Shape[]>();
    const [bgBorderShape, setBgBorderShape] = useState<Shape[]>();
    const drawBox = useRef<boolean>(true);
    const attributeRefs = useRef<AttributeRefs[]>([]);
    const materialRef = useRef<MeshPhysicalMaterial>(new MeshPhysicalMaterial());
    const shapeGeometryRef = useRef<ShapeGeometry>(new ShapeGeometry());
    const scratchedMetal = useTexture(scrtchedMetalImg);
    useScratchedMetalRoughness(backgroundRef, materialRef, shapeGeometryRef, cardBounds, !!bgShape);

    useFrame(() => {
        if (
            drawBox.current &&
            attributeRefs.current.length > 0 &&
            attributeRefs.current.every(
                ({ label, value }) =>
                    label.geometry.boundingBox &&
                    label.geometry.boundingBox.min.x < width &&
                    value.geometry.boundingBox &&
                    value.geometry.boundingBox.min.x < width
            )
        ) {
            // create an array of sizes per attribute pair
            const sizes: AttributeSize[] = [];
            attributeRefs.current.forEach(({ label, value }, i) => {
                const labelWidth = calculateWidth(label);
                const valueWidth = calculateWidth(value);
                const textWidth = labelWidth + valueWidth + VALUE_LEFT_MARGIN;
                value.position.x = labelWidth + VALUE_LEFT_MARGIN;
                sizes.push({ refIndex: i, size: textWidth });
            });

            // find a permutation where all the pairs fit
            let firstRow: AttributeSize[], secondRow: AttributeSize[], removeIndices: AttributeSize[];
            try {
                [firstRow, secondRow, removeIndices] = findSizePermutation(sizes);
            } catch (e) {
                console.error(e);
                [firstRow, secondRow, removeIndices] = [[], [], sizes];
            }

            removeIndices.forEach(({ refIndex }) => {
                attributeRefs.current[refIndex].group.visible = false;
            });

            if (firstRow.length) {
                const topGroupSize =
                    firstRow.reduce(sumSizes, 0) + HORIZONTAL_INNER_PADDING * 2 * (firstRow.length - 1);
                const bottomGroupSize =
                    secondRow.reduce(sumSizes, 0) + HORIZONTAL_INNER_PADDING * 2 * (secondRow.length - 1);
                const topGroupLeftX = -topGroupSize / 2;
                // bottom grouping left start should be under the center of the first
                // attribute in the row above it OR centered on screen, whichever is further
                // left
                const firstElCenter = RADIUS + topGroupLeftX + firstRow[0].size / 2;
                const bottomGroupCenteredLeftX = -bottomGroupSize / 2;
                const bottomGroupLeftX =
                    firstElCenter < bottomGroupCenteredLeftX ? firstElCenter : bottomGroupCenteredLeftX;

                // next, position the pairs based on count and size
                const topRowCenterY = -0.05;
                const bottomRowCenterY = -0.245;
                const bgShapes: Shape[] = [];
                const bgBorderShapes: Shape[] = [];

                // draw a row even 1 or 2 even elements
                const drawEvenRow =
                    (centerY: number, leftX: number) =>
                    ({ refIndex, size }: { refIndex: number; size: number }, i: number, row: AttributeSize[]): void => {
                        const group = attributeRefs.current[refIndex].group;
                        if (i === 0) {
                            group.position.x = leftX;
                            group.position.y = centerY;
                            bgShapes.push(drawBackground(leftX, size, centerY));
                            bgBorderShapes.push(drawBackground(leftX, size, centerY, BLACK_BORDER_SIZE));
                        } else if (i === 1) {
                            const textLeft = leftX + row[0].size + HORIZONTAL_INNER_PADDING * 2;
                            group.position.x = textLeft;
                            group.position.y = centerY;
                            bgShapes.push(drawBackground(textLeft, size, centerY));
                            bgShapes.push(connectNodes(leftX, centerY, row[0].size, textLeft, centerY));
                            bgBorderShapes.push(drawBackground(textLeft, size, centerY, BLACK_BORDER_SIZE));
                            bgBorderShapes.push(
                                connectNodes(leftX, centerY, row[0].size, textLeft, centerY, BLACK_BORDER_SIZE)
                            );
                        }
                    };
                firstRow.forEach(
                    firstRow.length === 2
                        ? drawEvenRow(topRowCenterY, topGroupLeftX)
                        : ({ refIndex, size }, i, row) => {
                              const group = attributeRefs.current[refIndex].group;
                              if (i === 0) {
                                  group.position.x = topGroupLeftX;
                                  group.position.y = topRowCenterY;
                                  bgShapes.push(drawBackground(topGroupLeftX, size, topRowCenterY));
                                  bgBorderShapes.push(
                                      drawBackground(topGroupLeftX, size, topRowCenterY, BLACK_BORDER_SIZE)
                                  );
                              } else if (i === 1) {
                                  const textLeft = topGroupLeftX + row[0].size + HORIZONTAL_INNER_PADDING * 2;
                                  group.position.x = textLeft;
                                  bgShapes.push(drawBackground(textLeft, size, 0));
                                  bgBorderShapes.push(drawBackground(textLeft, size, 0, BLACK_BORDER_SIZE));
                                  bgShapes.push(connectNodes(topGroupLeftX, topRowCenterY, row[0].size, textLeft, 0));
                                  bgBorderShapes.push(
                                      connectNodes(
                                          topGroupLeftX,
                                          topRowCenterY,
                                          row[0].size,
                                          textLeft,
                                          0,
                                          BLACK_BORDER_SIZE
                                      )
                                  );
                              } else if (i === 2) {
                                  const textLeft =
                                      topGroupLeftX + row[0].size + row[1].size + HORIZONTAL_INNER_PADDING * 4;
                                  group.position.x = textLeft;
                                  group.position.y = topRowCenterY;
                                  bgShapes.push(drawBackground(textLeft, size, topRowCenterY));
                                  bgBorderShapes.push(drawBackground(textLeft, size, topRowCenterY, BLACK_BORDER_SIZE));

                                  const node1LeftX = attributeRefs.current[row[1].refIndex].group.position.x;
                                  bgShapes.push(connectNodes(node1LeftX, 0, row[1].size, textLeft, topRowCenterY));
                                  bgBorderShapes.push(
                                      connectNodes(
                                          node1LeftX,
                                          0,
                                          row[1].size,
                                          textLeft,
                                          topRowCenterY,
                                          BLACK_BORDER_SIZE
                                      )
                                  );
                              }
                          }
                );
                secondRow.forEach(drawEvenRow(bottomRowCenterY, bottomGroupLeftX));

                setBgShape(bgShapes);
                setBgBorderShape(bgBorderShapes);
            }

            drawBox.current = false;
        }
    });

    // return a callback ref that allows the component to be added to an array of {label, value, group} refs
    const setRef = (attributeIndex: number, type: 'label' | 'value' | 'group'): ((ref: Mesh & Group) => void) => {
        // if the item in our array doesn't exist yet, add it
        if (attributeRefs.current.length === attributeIndex) {
            attributeRefs.current[attributeIndex] = { label: new Mesh(), value: new Mesh(), group: new Group() };
        }
        return (ref: Group & Mesh) => {
            attributeRefs.current[attributeIndex][type] = ref;
        };
    };

    useLayoutEffect(() => {
        const [xMin, xMax] = cardBounds.x;
        const [yMin, yMax] = cardBounds.y;
        const validBounds = xMin !== 0 || xMax !== 0;
        if (shapeGeometryRef.current && materialRef.current && validBounds) {
            // recalculate UV
            const newUv = [];
            const position = shapeGeometryRef.current.getAttribute('position');
            for (let i = 0; i < position.count; i++) {
                const index = 3 * i;
                const x = position.array[index];
                newUv.push(MathUtils.mapLinear(x, xMin, xMax, 0, 1));
                const y = position.array[index + 1];
                newUv.push(MathUtils.mapLinear(y, yMin, yMax, 0, 1));
            }
            shapeGeometryRef.current.setAttribute('uv', new BufferAttribute(new Float32Array(newUv), 2));

            materialRef.current.roughnessMap = scratchedMetal;
        }
    });

    return (
        <group ref={groupRef} position={[0, 0, 0]} scale={1.05}>
            <mesh visible={!!bgBorderShape} position={[0, 0, 0]}>
                <shapeGeometry attach="geometry" args={[bgBorderShape]} />
                <meshPhysicalMaterial
                    emissive={legendary ? legendaryWhite : 'black'}
                    emissiveIntensity={legendary ? 1 : 0}
                    color={legendary ? legendaryWhite : 'black'}
                />
            </mesh>
            <mesh visible={!!bgShape} ref={backgroundRef} position={[0, 0, 0.001]}>
                <shapeGeometry ref={shapeGeometryRef} attach="geometry" args={[bgShape]} />
                {legendary ? (
                    <meshPhysicalMaterial ref={materialRef} color={legendaryCard} metalness={1} transparent />
                ) : (
                    <meshBasicMaterial color="white" />
                )}
            </mesh>
            {attributes.map(({ value, label }, i) => (
                <group ref={setRef(i, 'group')} key={label} position={[0, 0, 0.002]}>
                    <Text
                        ref={setRef(i, 'label')}
                        position={[0, 0.002, 0]}
                        font={celophane}
                        fontSize={0.037}
                        letterSpacing={-0.035}
                        anchorX="left"
                        color={offblack}
                        renderOrder={1}
                    >
                        {label}:
                    </Text>
                    <Text
                        ref={setRef(i, 'value')}
                        position={[0, -0.008, 0]}
                        anchorX="left"
                        font={garamondNarrowBook}
                        fontSize={0.102}
                        letterSpacing={-0.05}
                        color={offblack}
                        renderOrder={1}
                    >
                        {value}
                    </Text>
                </group>
            ))}
        </group>
    );
};
